import React, { useRef, useCallback, useEffect, useState } from 'react';
import cn from 'classnames';
import request from 'utils/request_ajax';
import { routes } from 'constants/config';
import { Button, ButtonGroup } from 'ui';
import useEventListener from 'components/shared/hooks/useEventListener';

const LEFT_ARROW = 37;
const RIGHT_ARROW = 39;

export const LessonNavigation = props => {
  const {
    isFirstSection,
    previousLearningPath,
    hasMissedAnswers,
    nextLearningPath,
    isLastSection,
    isPreviewing,
    shouldRate,
    finished,
    finishLearningPath,
    reportCardLearningPath,
    ratingsLearningPath,
    scormRegistration,
    isHidden,
  } = props;

  const [isNavigationHidden, setIsNavigationHidden] = useState(isHidden);
  const backwardRef = useRef();
  const forwardRef = useRef();

  const timeoutRef = useRef(null);

  const stopTimeout = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  const getUpdatedScormMessage = useCallback(event => {
    const { isTrusted, data } = event || {};

    const parsedData = typeof data === 'string' ? JSON.parse(data) : data;
    const { payload } = parsedData || null;

    if (!isTrusted || !payload) return;
    if (payload.completion === 'completed') {
      stopTimeout();
      setIsNavigationHidden(false);
    }
  }, []);

  const getRegistrationStatus = useCallback(() => {
    request
      .get(routes.SCORM_REGISTRATION_STATUS(scormRegistration))
      .then(response => {
        if (response.is_complete) {
          stopTimeout();
          setIsNavigationHidden(false);
          window.removeEventListener('message', getUpdatedScormMessage);
        } else if (isNavigationHidden) {
          timeoutRef.current = setTimeout(getRegistrationStatus, 2000);
        }
      })
      .fail(response => {
        console.error(response);
      });
  }, [scormRegistration, getUpdatedScormMessage, isNavigationHidden]);

  useEffect(() => {
    if (scormRegistration && isHidden) {
      timeoutRef.current = setTimeout(getRegistrationStatus, 0);
      window.addEventListener('message', getUpdatedScormMessage);
    }
    return () => {
      clearTimeout(timeoutRef.current);
      window.removeEventListener('message', getUpdatedScormMessage);
    };
  }, [
    scormRegistration,
    isHidden,
    getRegistrationStatus,
    getUpdatedScormMessage,
  ]);

  const handler = useCallback(
    e => {
      const { keyCode, ctrlKey, shiftKey, metaKey } = e;

      const specialKey = ctrlKey || shiftKey || metaKey;
      const currentFocusedElement =
        document.activeElement.tagName.toLowerCase();
      const isInputFocused = currentFocusedElement === 'input';
      const isTextareaFocused = currentFocusedElement === 'textarea';
      const isSelectFocused = currentFocusedElement === 'select';

      if (
        specialKey ||
        isInputFocused ||
        isTextareaFocused ||
        isSelectFocused
      ) {
        return;
      }

      // Click element when correct arrow key is used and element is renderend on the screen
      if (keyCode === LEFT_ARROW && backwardRef.current) {
        stopTimeout();
        backwardRef.current.click();
      } else if (
        keyCode === RIGHT_ARROW &&
        forwardRef.current &&
        !isLastSection
      ) {
        stopTimeout();
        forwardRef.current.click();
      }
    },
    [isLastSection],
  );

  useEventListener('keydown', handler);

  const renderBackwardButton = () => {
    if (!isFirstSection) {
      const PREVIOUS_TEXT = t('lesson_navigation.previous');
      return (
        <Button
          className="back LessonNavigation-button trk_button_readiness_lesson-nav-previous-section"
          type="button"
          url={previousLearningPath}
          ref={backwardRef}
          ariaLabel={PREVIOUS_TEXT}
        >
          {PREVIOUS_TEXT}
        </Button>
      );
    }

    return <></>;
  };

  const renderForwardButton = () => {
    if (hasMissedAnswers) {
      return <></>;
    }

    let attributes = { url: nextLearningPath };
    let text = 'lesson_navigation.next';

    if (isLastSection) {
      if (isPreviewing) {
        return <></>;
      }

      let dataBehaviors;

      if (!window.location.href.includes('/journey/')) {
        dataBehaviors = shouldRate
          ? 'scorm_finished_link ask_for_learner_feedback'
          : 'scorm_finished_link';
      }

      attributes = finished
        ? { url: reportCardLearningPath }
        : {
            'data-ratings-url': ratingsLearningPath,
            'data-behavior': dataBehaviors,
            'data-method': 'post',
            url: finishLearningPath,
          };
      text = finished
        ? 'progress.view_report_card'
        : 'lesson_navigation.complete';
    }

    return (
      <Button
        id="LessonNavigationForwardButton"
        className={`LessonNavigation-button forward ${
          isLastSection
            ? 'trk_button_readiness_lesson-nav-complete-lesson'
            : 'trk_button_readiness_lesson-nav-next-section'
        }`}
        customColor
        type="button"
        ref={forwardRef}
        ariaLabel={t(text)}
        {...attributes}
      >
        {t(text)}
      </Button>
    );
  };

  return (
    <div
      className={cn('LessonNavigation', {
        hidden: isNavigationHidden,
      })}
    >
      <ButtonGroup alignEnd className="LessonNavigation-button-container">
        {renderBackwardButton()}
        {renderForwardButton()}
      </ButtonGroup>
    </div>
  );
};

export default LessonNavigation;
